import { useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDur, fmtTime } from "@/lib/api";
import { usePoll, PageHeader, Panel, Pill, Stat, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";

const RANGES = [1, 7, 30];
const STATE = { primary: ["bg-emerald-500/70", "on primary"], backup: ["bg-amber-500", "on backup"], off_air: ["bg-red-500", "off air"], stopped: ["bg-slate-600", "stopped"], unknown: ["bg-slate-800", "no data"] };
const GRADE = { good: "green", fair: "amber", weak: "red" };

function Timeline({ r, days }) {
  return (
    <div data-testid={`timeline-${r.slug}`} className="relative h-3 w-full rounded-sm bg-slate-900 overflow-hidden border border-slate-800">
      {r.timeline.map((t, i) => (
        <div key={i} data-testid={`timeline-seg-${r.slug}-${t.state}`} title={`${STATE[t.state]?.[1] || t.state}: ${fmtTime(t.start, true)} → ${fmtTime(t.end, true)} (${fmtDur(t.seconds)})`}
          className={`absolute top-0 h-full ${STATE[t.state]?.[0] || "bg-slate-700"}`}
          style={{ left: `${t.left}%`, width: `${t.width}%`, minWidth: t.state === "off_air" || t.state === "backup" ? 2 : 0 }} />
      ))}
    </div>
  );
}

function Legend({ days }) {
  return (
    <div className="flex items-center gap-4 text-[11px] text-slate-400 mb-3" data-testid="timeline-legend">
      {Object.entries(STATE).map(([k, [c, l]]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-2.5 w-4 rounded-sm ${c}`} />{l}</span>)}
      <span className="ml-auto font-mono text-slate-500">← {days}d ago · now →</span>
    </div>
  );
}

export default function Reliability() {
  const [days, setDays] = useState(7);
  const { data: d } = usePoll(`/admin/digest?days=${days}`, 30000);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/admin/digest/send?days=${days}`); toast.success("Digest sent through Alerts"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const t = d?.totals;
  return (
    <div>
      <PageHeader title="Failure digest" sub="Which channels and sources failed most, and how long channels were off air. Sent automatically every Monday 09:00 New York."
        right={<>
          <div className="flex gap-1 mr-2" data-testid="digest-range">
            {RANGES.map((r) => <button key={r} data-testid={`digest-range-${r}`} onClick={() => setDays(r)} className={`px-2.5 py-1 rounded text-xs font-mono ${days === r ? "bg-cyan-500/20 text-cyan-300" : "text-slate-400 hover:text-slate-100"}`}>{r}d</button>)}
          </div>
          <Button data-testid="digest-send-btn" variant="outline" className="border-slate-700" disabled={busy} onClick={send}><Send size={14} /> Send digest now</Button>
        </>} />
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <Stat label="Channels with failures" value={`${t.channels_with_failures} / ${t.channels}`} testid="digest-failing" tone={t.channels_with_failures ? "text-amber-400" : "text-emerald-400"} />
          <Stat label="Source failures" value={t.source_failures} testid="digest-source-failures" />
          <Stat label="Switches" value={t.switches} />
          <Stat label="Off-air events" value={t.off_air_events} testid="digest-off-air-events" tone={t.off_air_events ? "text-red-400" : undefined} />
          <Stat label="Total off air" value={fmtDur(t.off_air_seconds)} testid="digest-off-air-total" />
        </div>
      )}
      {d && d.channels.length === 0 ? <Empty>No channels.</Empty> : (
        <Panel title={`Ranking · last ${days} day(s)`} testid="digest-table">
          <Legend days={days} />
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              {["#", "Channel", "Off air", "Off-air events", "Source failures", "Switches", "Worst source", "Source scores (7d · failures)", "Availability"].map((h) => <th key={h} className="py-2 pr-3 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {(d?.channels || []).map((r, i) => [
                <tr key={r.id} data-testid={`digest-row-${r.slug}`} className="font-mono">
                  <td className="py-2 pr-3 text-slate-500">{i + 1}</td>
                  <td className="py-2 pr-3"><Link to={`/channels/${r.id}`} className="text-cyan-300 hover:underline">{r.slug}</Link> <span className="text-slate-500 font-sans">{r.name}</span></td>
                  <td className={`py-2 pr-3 ${r.off_air_seconds ? "text-red-400" : "text-slate-400"}`}>{r.off_air_human}</td>
                  <td className="py-2 pr-3 text-slate-300">{r.off_air}</td>
                  <td className="py-2 pr-3 text-slate-300">{r.source_down}</td>
                  <td className="py-2 pr-3 text-slate-300">{r.switch}</td>
                  <td className="py-2 pr-3">{r.worst_source ? <Pill tone="amber">#{r.worst_source.index} {r.worst_source.label} · {r.worst_source.failures}</Pill> : <span className="text-slate-600">—</span>}</td>
                  <td className="py-2 pr-3"><div className="flex flex-wrap gap-1">{r.sources.map((s) => <Pill key={s.index} tone={GRADE[s.grade]} testid={`digest-score-${r.slug}-${s.index}`}>#{s.index} {s.score} · {s.failures}f</Pill>)}</div></td>
                  <td className={`py-2 pr-3 ${r.availability_pct < 99.9 ? "text-amber-400" : "text-emerald-400"}`}>{r.availability_pct}%</td>
                </tr>,
                <tr key={`${r.id}-tl`} className="border-b border-slate-800/60"><td /><td colSpan={8} className="pb-3 pr-3"><Timeline r={r} days={days} /></td></tr>,
              ])}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
